import { useState, useCallback } from 'react';
import { useAdminGetProductTrials } from '@/api/auth/admin/admin';
import type { ProductTrialResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Trash2 } from 'lucide-react';
import { CreateTrialDialog } from './CreateTrialDialog';
import { DeleteTrialDialog } from './DeleteTrialDialog';
import { formatDistanceToNow, parseISO } from 'date-fns';
import type { EntitlementRule } from '@/api/auth/missionImpactAuthAPI.schemas';

/**
 * Typed trial with properly parsed entitlement rule
 */
interface TypedProductTrial extends Omit<ProductTrialResponse, 'rule'> {
  rule?: EntitlementRule;
}

/**
 * Props for the ProductTrialsForm component
 */
interface ProductTrialsFormProps {
  /** The product ID */
  productId: string;
}

/**
 * Component for managing product trials - displays existing trials and allows creating new ones
 */
export function ProductTrialsForm({ productId }: ProductTrialsFormProps) {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [deleteDialogState, setDeleteDialogState] = useState<{
    isOpen: boolean;
    trialId: string | null;
    trialToken?: string;
  }>({
    isOpen: false,
    trialId: null,
    trialToken: undefined,
  });

  // Memoize the selector function to prevent unnecessary re-renders
  const selectTypedTrials = useCallback((data: any): TypedProductTrial[] => {
    // The API returns a typed, polymorphic EntitlementRule directly.
    return (data?.data?.trials || []) as TypedProductTrial[];
  }, []);

  // Fetch trials for this product and transform to typed trials
  const {
    data: trials,
    isLoading: isLoadingTrials,
    error: trialsError,
  } = useAdminGetProductTrials(productId, {
    query: {
      enabled: !!productId,
      select: selectTypedTrials,
    },
  });

  /**
   * Handle opening the delete dialog
   */
  const handleDeleteClick = useCallback((trialId: string, trialToken?: string) => {
    setDeleteDialogState({
      isOpen: true,
      trialId,
      trialToken,
    });
  }, []);

  /**
   * Handle closing the delete dialog
   */
  const handleDeleteDialogClose = useCallback((open: boolean) => {
    if (!open) {
      setDeleteDialogState({
        isOpen: false,
        trialId: null,
        trialToken: undefined,
      });
    }
  }, []);

  /**
   * Handle opening the create dialog
   */
  const handleCreateClick = useCallback(() => {
    setIsCreateDialogOpen(true);
  }, []);

  /**
   * Format rule for display
   */
  const formatRule = useCallback((rule?: EntitlementRule): string => {
    if (!rule) return 'N/A';
    if ('type' in rule && rule.type === 'cumulative') {
      return `Cumulative: ${rule.count}`;
    }
    if ('type' in rule && rule.type === 'minimum') {
      return `Minimum: ${rule.minimum}`;
    }
    return 'Unknown';
  }, []);

  /**
   * Format duration for display
   */
  const formatDuration = useCallback((durationDays?: number | null): string => {
    if (!durationDays) return 'Perpetual';
    return `${durationDays} days`;
  }, []);

  /**
   * Format redemption date for display
   */
  const formatRedemptionDate = useCallback((date?: string | null): string => {
    if (!date) return 'N/A';
    try {
      const trimmed = date.trim();
      const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(trimmed);
      const normalized = hasTimezone ? trimmed : `${trimmed}Z`;
      const parsed = parseISO(normalized);
      if (Number.isNaN(parsed.getTime())) {
        return date;
      }
      const localized = new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(parsed);
      const relative = formatDistanceToNow(parsed, { addSuffix: true });
      return `${localized} (${relative})`;
    } catch {
      return date;
    }
  }, []);

  // Handle loading state
  if (isLoadingTrials) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-8 w-[200px]" />
          <Skeleton className="h-4 w-[300px]" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-10 w-[150px]" />
            <Skeleton className="h-[200px] w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Handle error state
  if (trialsError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Product Trials</CardTitle>
          <CardDescription>Failed to load trials</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center text-destructive py-8">
            <p>Failed to load trials: {(trialsError as any)?.message || 'Unknown error'}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Product Trials</CardTitle>
            <CardDescription>
              Manage trial tokens for this product. Each trial can be redeemed once.
            </CardDescription>
          </div>
          <Button onClick={handleCreateClick}>
            <Plus className="h-4 w-4 mr-2" />
            Create Trial
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!trials || trials.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No trials created yet. Create your first trial to get started.</p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Token</TableHead>
                  <TableHead>Rule</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Redeemed</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trials.map((trial) => (
                  <TableRow key={trial.id}>
                    <TableCell className="font-mono text-sm">
                      {trial.token ? (
                        <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem]">
                          {trial.token}
                        </code>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell>{formatRule(trial.rule)}</TableCell>
                    <TableCell>{formatDuration(trial.durationDays)}</TableCell>
                    <TableCell>
                      {trial.redeemedUtc ? (
                        <Badge variant="secondary">Redeemed</Badge>
                      ) : (
                        <Badge variant="default">Available</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatRedemptionDate(trial.redeemedUtc)}
                    </TableCell>
                    <TableCell>
                      {!trial.redeemedUtc && trial.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(trial.id!, trial.token)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <CreateTrialDialog
        productId={productId}
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
      />

      <DeleteTrialDialog
        productId={productId}
        trialId={deleteDialogState.trialId}
        trialToken={deleteDialogState.trialToken}
        open={deleteDialogState.isOpen}
        onOpenChange={handleDeleteDialogClose}
      />
    </Card>
  );
}
